import React from "react";
import { Checkbox, Field, Label, Radio, RadioGroup, Slider } from "@fluentui/react-components";
import type { LineStyleOption } from "../visualConfig";
import { type ChartStyleSectionProps, useSliderStyles } from "./shared";

interface LineAreaStyleProps extends ChartStyleSectionProps {
  isArea: boolean;
}

const LineAreaStyle: React.FC<LineAreaStyleProps> = ({ chartStyle: cs, onStyleChange, isArea }) => {
  const styles = useSliderStyles();
  return (
    <>
      {/* ── Stacking ─────────────────────────────────────────── */}
      <div className={styles.sliderField}>
        <Label size="small">Stacking</Label>
        <RadioGroup
          layout="horizontal"
          value={cs.stacking ?? 'off'}
          onChange={(_, data) => onStyleChange({ stacking: (data.value as 'off' | 'normal' | 'percent') ?? 'off' })}
        >
          <Radio value="off" label="None" />
          <Radio value="normal" label="Stacked" />
          <Radio value="percent" label="100% Stacked" />
        </RadioGroup>
      </div>

      <Checkbox
        label="Show markers"
        checked={cs.showMarkers ?? true}
        onChange={(_, data) => onStyleChange({ showMarkers: !!data.checked })}
      />
      {(cs.showMarkers ?? true) && (
        <div className={styles.sliderField}>
          <Label size="small">Marker Size</Label>
          <div className={styles.sliderRow}>
            <Slider
              min={2} max={12} step={1}
              value={cs.markerSize ?? 3}
              onChange={(_, data) => onStyleChange({ markerSize: data.value })}
              style={{ flex: 1 }}
            />
            <span className={styles.sliderValue}>{cs.markerSize ?? 3}px</span>
          </div>
        </div>
      )}
      <Field label="Line Style">
        <RadioGroup
          layout="horizontal"
          value={cs.lineStyle ?? "regular"}
          onChange={(_, data) => onStyleChange({ lineStyle: (data.value as LineStyleOption) ?? "regular" })}
        >
          <Radio value="regular" label="Regular" />
          <Radio value="smooth" label="Smooth" />
          <Radio value="step" label="Step" />
        </RadioGroup>
      </Field>
      <div className={styles.sliderField}>
        <Label size="small">Line Width</Label>
        <div className={styles.sliderRow}>
          <Slider
            min={1} max={6} step={0.5}
            value={cs.lineWidth ?? 2}
            onChange={(_, data) => onStyleChange({ lineWidth: data.value })}
            style={{ flex: 1 }}
          />
          <span className={styles.sliderValue}>{cs.lineWidth ?? 2}px</span>
        </div>
      </div>
      {isArea && (
        <div className={styles.sliderField}>
          <Label size="small">Area Opacity</Label>
          <div className={styles.sliderRow}>
            <Slider
              min={0} max={100} step={5}
              value={Math.round((cs.areaOpacity ?? 0.25) * 100)}
              onChange={(_, data) => onStyleChange({ areaOpacity: data.value / 100 })}
              style={{ flex: 1 }}
            />
            <span className={styles.sliderValue}>
              {Math.round((cs.areaOpacity ?? 0.25) * 100)}%
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default LineAreaStyle;